import type { GraphEditorState } from '../../types';
import type { OperationNodeSpec } from '../../../graph/domain/types';
import { generateGuid } from '../../../graph/utils/id';
import { recomputeDerivation } from '../../recomputeDerivation';
import { propagateDownstream } from '@/commands/helpers/propagateDownstream';
import type { ConversionPlan } from './planConversion';

type GraphData = GraphEditorState['graphData'];

/**
 * Execute a `ConversionPlan` against a graph.
 *
 * `graphData` is mutated, so callers must hand in a clone (`cloneGraphData`).
 * The returned value is the derivation-refreshed graph to put back in the store.
 *
 * The operation's id is never re-created — group `stepIds`, `cycleRanges`,
 * composition `stepId` and the backend `stepVertexId` all reference it.
 */
export function applyConversion(
  graphData: GraphData,
  plan: ConversionPlan,
  userPatch: Partial<OperationNodeSpec> = {},
): GraphData {
  if (plan.blockers.length > 0) {
    throw new Error(plan.blockers[0]);
  }

  const operation = graphData.operationNodes[plan.operationId];
  if (!operation) throw new Error(`Operation not found: ${plan.operationId}`);

  // 1. Drop every edge the operation owns, remembering ids so an unchanged
  //    endpoint pair keeps its identity across the rebuild.
  const edgeIdByPair = new Map<string, string>();
  for (const edgeKey of plan.edgeKeysToRemove) {
    const edge = graphData.flowEdges[edgeKey];
    if (!edge) continue;
    edgeIdByPair.set(pairKey(edge.sourceId, edge.targetId), edge.id ?? edgeKey);
    delete graphData.flowEdges[edgeKey];
  }

  // 2. Remove pruned materials, cascaded operations, and anything touching them.
  const doomedCells = new Set<string>([
    ...plan.materialsToDelete,
    ...plan.operationsToDelete,
  ]);
  for (const materialId of plan.materialsToDelete) {
    delete graphData.materialNodes[materialId];
  }
  for (const operationId of plan.operationsToDelete) {
    delete graphData.operationNodes[operationId];
  }
  for (const [edgeKey, edge] of Object.entries(graphData.flowEdges)) {
    if (doomedCells.has(edge.sourceId) || doomedCells.has(edge.targetId)) {
      delete graphData.flowEdges[edgeKey];
    }
  }

  // 3. Create the materials the target archetype needs.
  const now = new Date().toISOString();
  for (const { id, spec } of plan.materialsToCreate) {
    graphData.materialNodes[id] = {
      ...spec,
      id,
      vertexId: id,
      createdAt: spec.createdAt ?? now,
    };
  }

  // 4. Patch surviving materials that changed role.
  for (const { id, patch } of plan.materialPatches) {
    const existing = graphData.materialNodes[id];
    if (!existing) continue;
    graphData.materialNodes[id] = { ...existing, ...patch };
  }

  // 5. Re-insert the operation's edges in canonical order. Record insertion
  //    order is what `hydrateFromSnapshot` and the backend read positionally.
  for (const { sourceId, targetId } of plan.edgesToAdd) {
    if (!endpointExists(graphData, sourceId) || !endpointExists(graphData, targetId)) {
      continue;
    }
    const reusedId = edgeIdByPair.get(pairKey(sourceId, targetId));
    const edgeId = reusedId ?? generateGuid();
    graphData.flowEdges[edgeId] = { id: edgeId, edgeId, sourceId, targetId };
  }

  // 6. Write the operation spec: caller's form fields first, then the fields the
  //    conversion owns, then clear whatever the new archetype must not carry.
  const converted: OperationNodeSpec = {
    ...operation,
    ...userPatch,
    ...plan.specPatch,
  };
  for (const field of plan.clearFields) {
    delete converted[field];
  }
  graphData.operationNodes[plan.operationId] = converted;

  // 7. Drop deleted cells out of group membership and cycle ranges.
  pruneGroups(graphData, doomedCells);

  // 8. Rebuild composition for the new archetype, then push quantities /
  //    identity downstream from every surviving input.
  const refreshed = recomputeDerivation(graphData);

  const inputIds = plan.edgesToAdd
    .filter((edge) => edge.targetId === plan.operationId)
    .map((edge) => edge.sourceId);
  for (const inputId of inputIds) {
    if (!refreshed.materialNodes[inputId]) continue;
    propagateDownstream(refreshed, inputId, {
      fields: { identity: true, quantity: true, constituents: true },
    });
  }

  return refreshed;
}

function pairKey(sourceId: string, targetId: string): string {
  return `${sourceId}->${targetId}`;
}

function endpointExists(graphData: GraphData, cellId: string): boolean {
  return Boolean(graphData.materialNodes[cellId] || graphData.operationNodes[cellId]);
}

function pruneGroups(graphData: GraphData, doomedCells: Set<string>): void {
  if (doomedCells.size === 0) return;

  for (const [groupId, group] of Object.entries(graphData.groups)) {
    const stepIds = group.stepIds?.filter((id) => !doomedCells.has(id)) ?? [];
    const cycleRanges = group.cycleRanges?.map((range) => ({
      ...range,
      memberCellIds: range.memberCellIds?.filter((id) => !doomedCells.has(id)),
    }));

    const stepIdsChanged = stepIds.length !== (group.stepIds?.length ?? 0);
    if (!stepIdsChanged && !group.cycleRanges) continue;

    graphData.groups[groupId] = { ...group, stepIds, cycleRanges };
  }
}
